import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import { modifier } from 'ember-modifier';
import CloseButton from 'garaje/components/close-button';
import ButtonGroup from 'garaje/components/ui/button-group';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import PhoneInput from 'garaje/pods/components/phone-input/component';
import { isInvalidE164, isInvalidEmail } from 'garaje/utils/contact-patterns';

interface Signature {
  Args: {
    isOpen: boolean;
    currentEmail: string;
    currentPhone: string;
    onSave: (email: string, phone: string) => void;
    onCancel: () => void;
  };
}

export default class HrisDashboardNotifyContactsModal extends Component<Signature> {
  titleId = `${guidFor(this)}-title`;
  emailInputId = `${guidFor(this)}-email`;
  emailErrorId = `${guidFor(this)}-email-error`;

  @tracked email = '';
  @tracked phone = '';

  // Reseed + refocus whenever the modal (re)opens. `{{#if @isOpen}}` re-mounts the
  // form element, so this fires each open with the latest args.
  seedOnOpen = modifier((el: Element) => {
    if (el instanceof HTMLElement) {
      this.email = this.args.currentEmail;
      this.phone = this.args.currentPhone;
      const input = el.querySelector('input');
      if (input instanceof HTMLElement) input.focus();
    }
  });

  get emailInvalid(): boolean {
    return isInvalidEmail(this.email);
  }

  get phoneInvalid(): boolean {
    return isInvalidE164(this.phone);
  }

  get saveDisabled(): boolean {
    return this.emailInvalid || this.phoneInvalid;
  }

  @action
  setEmail(event: Event): void {
    this.email = (event.target as HTMLInputElement).value;
  }

  // Explicit close hook: drop the local draft before the parent flips @isOpen.
  // seedOnOpen re-seeds on remount, so this is belt-and-suspenders — it keeps the
  // "cancel discards typed input" invariant even if a future refactor removes the
  // `{{#if @isOpen}}` gate and the component instance stops being torn down.
  @action
  cancel(): void {
    this.email = '';
    this.phone = '';
    this.args.onCancel();
  }

  @action
  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') this.cancel();
  }

  @action
  onSubmit(event: Event): void {
    event.preventDefault();
    if (this.saveDisabled) return;
    this.args.onSave(this.email.trim(), this.phone.trim());
  }

  <template>
    {{#if @isOpen}}
      <ModalDialog
        @clickOutsideToClose={{false}}
        @targetAttachment="center"
        @translucentOverlay={{true}}
        @onClose={{this.cancel}}
        @containerClass="!max-w-md"
      >
        <FocusTrapper>
          <form
            role="dialog"
            aria-modal="true"
            aria-labelledby={{this.titleId}}
            class="relative p-8"
            data-test-hris-notify-modal
            {{on "keydown" this.handleKeydown}}
            {{on "submit" this.onSubmit}}
            {{this.seedOnOpen}}
          >
            <div class="mb-4">
              <h2 id={{this.titleId}} class="text-xl font-semibold text-carbon-80 m-0">
                Notify contacts
              </h2>
              <p class="mt-1 text-sm text-carbon-60 m-0">
                Send an alert when a terminated employee's identity matches a visitor sign-in. Both fields are optional.
              </p>
              <p class="mt-2 text-xs text-carbon-50 m-0" data-test-hris-notify-clear-hint>
                This contact is shared with your visitor watch list. Leave a field blank to keep its current value —
                clear it from Security → Watch List.
              </p>
            </div>

            <div class="flex flex-col gap-4 mt-5">
              <div>
                <label
                  for={{this.emailInputId}}
                  class="block text-xs font-semibold text-carbon-40 uppercase tracking-wide mb-1"
                >
                  Notify email
                </label>
                <input
                  id={{this.emailInputId}}
                  type="email"
                  value={{this.email}}
                  placeholder="security@acme.com"
                  aria-invalid={{if this.emailInvalid "true" "false"}}
                  aria-describedby={{if this.emailInvalid this.emailErrorId}}
                  {{on "input" this.setEmail}}
                  class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-50
                    {{if this.emailInvalid 'border-papaya-50 focus:ring-papaya-50' 'border-gray-200'}}"
                  data-test-hris-notify-email
                />
                {{#if this.emailInvalid}}
                  <p id={{this.emailErrorId}} class="mt-1 text-xs text-papaya-50 m-0" data-test-hris-notify-email-error>
                    Enter a valid email address.
                  </p>
                {{/if}}
              </div>

              {{! Standard garaje international phone field (country-flag picker), the
                  same component used on Security → Watch List. It two-way binds
                  @phoneNumber and emits E.164; `phoneInvalid` still gates Save. }}
              <div data-test-hris-notify-phone>
                <label class="block text-xs font-semibold text-carbon-40 uppercase tracking-wide mb-1">
                  Notify phone number
                </label>
                <PhoneInput
                  @phoneNumber={{this.phone}}
                  @label=""
                  @autocomplete="off"
                  @validated={{true}}
                  @hasErrors={{this.phoneInvalid}}
                  @errorLabel="Use E.164 format — a “+” and country code, e.g. +15551234567."
                />
              </div>
            </div>

            <div class="pt-6 flex justify-end">
              <ButtonGroup>
                <button
                  type="button"
                  class="btn btn--small btn--outline hover:bg-carbon-5"
                  data-test-hris-notify-cancel
                  {{on "click" this.cancel}}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={{this.saveDisabled}}
                  class="btn btn--small btn--red-outline {{if this.saveDisabled 'opacity-50 cursor-not-allowed'}}"
                  data-test-hris-notify-save
                >
                  Save
                </button>
              </ButtonGroup>
            </div>

            <CloseButton
              class="right-4 top-4 !bg-white !p-0 w-10 h-10"
              aria-label="Close"
              data-test-hris-notify-close
              @onClick={{this.cancel}}
            />
          </form>
        </FocusTrapper>
      </ModalDialog>
    {{/if}}
  </template>
}
